import { Fragment, ReactNode } from 'react';
import { Transition } from '@headlessui/react';
import { TypoHeading, TypoTitle } from './Typography.tsx';
import { DeleteSquare } from './Icons.tsx';

export interface SheetProps {
  open: boolean;
  title: string;
  heading?: string;
  onClose: () => void;
  children: ReactNode;
  /** Full-height sheet -- used by the reference sheet and the calculator. */
  full?: boolean;
  /** Light surface, matching the `alt` popper variant used on the landing page. */
  alt?: boolean;
  unmount?: boolean;
}

/**
 * The mobile counterpart of `Popover`.
 *
 * `Popover` positions with `react-popper` and its `Draggable` wrapper listens to
 * `mousedown`/`mousemove` only -- there is no touch path at all -- so the mobile
 * branch cannot be a class change. This is the one place in the layer that needs
 * JavaScript.
 */
const Sheet = ({ open, title, heading, onClose, children, full = false, alt = false, unmount }: SheetProps) => {
  const textColor = alt ? 'text-common-black' : 'text-neutral2-text';

  return (
    <Transition as={Fragment} show={open} unmount={unmount}>
      <div className="fixed inset-0 z-50 flex flex-col justify-end ml:flex-row ml:justify-end">
        <div
          className="absolute inset-0 bg-common-black/[.55]"
          onClick={onClose}
          aria-hidden
        />
        <div
          className={`relative flex flex-col shadow-popover
                      rounded-t-m-sheet
                      ml:h-full ml:max-h-none ml:w-[27rem] ml:max-w-[78%]
                      ml:rounded-l-m-sheet ml:rounded-tr-none
                      ${full ? 'h-[92dvh] max-h-none' : 'max-h-[85dvh]'}
                      ${alt ? 'bg-common-white' : 'bg-neutral2'}`}
        >
          <div className="mx-auto mt-2.5 h-1 w-9 shrink-0 rounded-full bg-shade-30 ml:hidden" />
          <div className="flex shrink-0 items-center justify-between gap-3 px-4 py-2">
            <div className="min-w-0">
              {heading ? (
                <TypoTitle className={`mb-1 ${textColor}`}>{heading}</TypoTitle>
              ) : null}
              <TypoHeading className={`block truncate ${textColor}`}>{title}</TypoHeading>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="h-11 w-11 shrink-0 rounded-md active:bg-shade-10"
            >
              <DeleteSquare className="mx-auto text-neutral2-canvas" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-tabbar-safe">{children}</div>
        </div>
      </div>
    </Transition>
  );
};

export default Sheet;
